import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTable } from "@/lib/store";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, Kicker, Chip, Avatar, Empty, Modal, Field, Area, Pick, Segmented } from "@/components/kit";
import { ROMAN, fmtDate, relTime, statusColor, MEETING_TYPES } from "@/lib/util";
import { DateField } from "@/components/DateField";
import { OnePager } from "@/components/OnePager";
import { CommitmentRow } from "@/pages/Ledger";
import { Printer, Send } from "lucide-react";

const TABS = ["The record", "Ledger", "Date poll"];

/* ------------------------------------------------- commitment check-in */
const CheckIn = ({ meeting }) => {
  const { me, activeMembers, canWrite } = useTable();
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () =>
    setRows((await api.get("/checkins", { params: { meetingId: meeting.id } })).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [meeting.id]);

  const byMember = (id) => rows.find((r) => r.memberId === id);

  const save = async (member) => {
    const d = draft[member.id] || {};
    setBusy(true);
    try {
      await api.post("/checkins", {
        meetingId: meeting.id, memberId: member.id,
        movement: d.movement || byMember(member.id)?.movement || "Held",
        note: d.note ?? byMember(member.id)?.note ?? "",
        commitmentText: member.tableCommitment || "",
      });
      setDraft({ ...draft, [member.id]: undefined });
      await load();
      toast.success("Check-in recorded.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="checkin-tab">
      <p className="text-[14px]" style={{ color: "var(--ink-mute)" }}>
        Every chapter, each member says how their standing commitment to the table moved. The trail
        stays on their member page.
      </p>
      {activeMembers.map((mem) => {
        const row = byMember(mem.id);
        const d = draft[mem.id];
        const mine = mem.id === me.memberId;
        const editable = canWrite && (me.isAdmin || mine);
        const movement = d?.movement ?? row?.movement ?? "Held";
        const note = d?.note ?? row?.note ?? "";
        const dirty = !!d;
        return (
          <Card key={mem.id} className="p-4" data-testid={`checkin-${mem.id}`}>
            <div className="flex items-center gap-3">
              <Avatar name={mem.name} color={mem.avatarColor} size={30} />
              <div className="min-w-0 flex-1">
                <div className="text-sm">{mem.name}{mine ? " · you" : ""}</div>
                <p className="text-[12.5px] mt-0.5" style={{ color: "var(--ink-mute)" }}>
                  {(mem.tableCommitment || "").split("\n").filter(Boolean).join(" · ") || "No commitment stated"}
                </p>
              </div>
              {row && !dirty && (
                <Chip
                  testId={`checkin-badge-${mem.id}`}
                  color={row.movement === "Moved" ? "var(--green)" : row.movement === "Stalled" ? "var(--rust)" : undefined}
                >
                  {row.movement}
                </Chip>
              )}
            </div>
            {editable ? (
              <div className="mt-3 space-y-3">
                <Segmented
                  options={["Moved", "Held", "Stalled"]}
                  value={movement}
                  testId={`checkin-movement-${mem.id}`}
                  onChange={(v) => setDraft({ ...draft, [mem.id]: { movement: v, note } })}
                />
                <Area
                  label="What moved, in a line"
                  value={note}
                  data-testid={`checkin-note-${mem.id}`}
                  onChange={(e) => setDraft({ ...draft, [mem.id]: { movement, note: e.target.value } })}
                />
                <button
                  className="btn-ink"
                  data-testid={`checkin-save-${mem.id}`}
                  disabled={busy}
                  onClick={() => save(mem)}
                >
                  {row ? "Update check-in" : "Record check-in"}
                </button>
              </div>
            ) : row?.note ? (
              <p className="text-sm mt-3" style={{ color: "var(--ink-soft)" }}>{row.note}</p>
            ) : (
              <p className="text-sm mt-3" style={{ color: "var(--ink-mute)" }}>Not checked in yet.</p>
            )}
          </Card>
        );
      })}
    </div>
  );
};

/* ------------------------------------------------------------- highlights */
const Highlights = ({ meeting }) => {
  const { me, highlights, refresh, canWrite, settings } = useTable();
  const emailPaused = !!settings?.email?.paused;
  const items = highlights.filter((h) => h.meetingId === meeting.id);
  const [form, setForm] = useState({ title: "", body: "", broadcast: false });
  const [busy, setBusy] = useState(false);
  const isAdmin = me.isAdmin && canWrite;

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/highlights", { meetingId: meeting.id, ...form });
      setForm({ title: "", body: "", broadcast: false });
      await refresh();
      toast.success(form.broadcast ? "Recorded and broadcast." : "Recorded.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const broadcast = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/highlights/${id}/broadcast`);
      await refresh();
      toast.success(`Sent to ${r.data.sent} members.`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      {isAdmin && (
        <Card data-testid="highlight-form">
          <Kicker>Record a highlight</Kicker>
          <div className="mt-4 space-y-3">
            <Field label="Title" value={form.title} data-testid="highlight-title" onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Area label="Body" rows={6} value={form.body} data-testid="highlight-body" onChange={(e) => setForm({ ...form, body: e.target.value })} />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="highlight-broadcast"
                disabled={emailPaused}
                checked={form.broadcast}
                onChange={(e) => setForm({ ...form, broadcast: e.target.checked })}
              />
              Broadcast to all members by email now
            </label>
            {emailPaused && (
              <p className="text-[12px]" data-testid="highlight-email-paused" style={{ color: "var(--amber)" }}>
                Email is paused for the forum — highlights are recorded here but nothing is sent.
              </p>
            )}
            <button className="btn-ink" data-testid="highlight-save" disabled={busy || !form.title} onClick={save}>
              {busy ? "Working" : "Record"}
            </button>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <Empty line="No highlights yet. The room writes this page." testId="highlights-empty" />
      ) : (
        items.map((h) => (
          <Card key={h.id} data-testid={`highlight-${h.id}`}>
            <h3 className="serif text-2xl leading-tight">{h.title}</h3>
            <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: "var(--ink-soft)" }}>{h.body}</p>
            <div className="flex gap-2 mt-4 items-center flex-wrap">
              {h.broadcastedAt ? (
                <Chip color="var(--green)" testId={`broadcast-badge-${h.id}`}>Broadcast {relTime(h.broadcastedAt)}</Chip>
              ) : (
                isAdmin && (
                  <button className="btn-quiet" data-testid={`broadcast-${h.id}`} disabled={busy || emailPaused} onClick={() => broadcast(h.id)}>
                    {emailPaused ? "Email paused" : "Broadcast →"}
                  </button>
                )
              )}
              {isAdmin && (
                <button
                  className="btn-quiet"
                  data-testid={`highlight-delete-${h.id}`}
                  onClick={async () => { await api.delete(`/highlights/${h.id}`); await refresh(); }}
                >
                  Delete
                </button>
              )}
            </div>
          </Card>
        ))
      )}
    </div>
  );
};

/* ------------------------------------------------------------- pre-work */
const PreWork = ({ meeting }) => {
  const { me, memberById, canWrite } = useTable();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ topic: "", wantFromGroup: "", priority: "Medium" });

  const load = async () => setItems((await api.get("/prework", { params: { meetingId: meeting.id } })).data);
  useEffect(() => { load(); }, [meeting.id]);

  const add = async () => {
    try {
      await api.post("/prework", { meetingId: meeting.id, ...form });
      setForm({ topic: "", wantFromGroup: "", priority: "Medium" });
      await load();
      toast.success("Submitted.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <p className="text-[14px]" style={{ color: "var(--ink-mute)" }}>Pre-submissions shape the agenda.</p>
      {canWrite && (
        <Card data-testid="prework-form">
          <Field label="Topic" value={form.topic} data-testid="prework-topic" onChange={(e) => setForm({ ...form, topic: e.target.value })} />
          <div className="mt-3"><Area label="What I want from the group" value={form.wantFromGroup} data-testid="prework-want" onChange={(e) => setForm({ ...form, wantFromGroup: e.target.value })} /></div>
          <div className="mt-3"><Pick label="Priority" options={["High", "Medium", "Low"]} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} /></div>
          <button className="btn-ink mt-4" data-testid="prework-save" disabled={!form.topic} onClick={add}>Submit</button>
        </Card>
      )}
      {items.length === 0 ? (
        <Empty line="Nothing submitted. The agenda is still open." testId="prework-empty" />
      ) : (
        items.map((p) => {
          const m = memberById(p.memberId);
          return (
            <Card key={p.id} className="p-4" data-testid={`prework-${p.id}`}>
              <div className="flex justify-between gap-3 items-start">
                <div className="min-w-0">
                  <div className="text-sm">{m?.name || "Member"}</div>
                  <p className="serif text-lg mt-0.5">{p.topic}</p>
                  {p.wantFromGroup && <p className="text-sm mt-2" style={{ color: "var(--ink-mute)" }}>{p.wantFromGroup}</p>}
                  <div className="flex gap-2 mt-2.5 flex-wrap">
                    <Chip color={p.priority === "High" ? "var(--rust)" : undefined}>{p.priority}</Chip>
                    {p.onAgenda && <Chip color="var(--green)">On agenda</Chip>}
                  </div>
                </div>
              </div>
              {me.isAdmin && canWrite && (
                <button
                  className="btn-quiet mt-3"
                  data-testid={`prework-agenda-${p.id}`}
                  onClick={async () => { await api.put(`/prework/${p.id}`, { onAgenda: !p.onAgenda }); await load(); }}
                >
                  {p.onAgenda ? "Remove from agenda" : "Put on agenda"}
                </button>
              )}
            </Card>
          );
        })
      )}
    </div>
  );
};

/* ------------------------------------------------------------- feedback */
const Feedback = ({ meeting }) => {
  const { canWrite } = useTable();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ rating: 8, worked: "", differently: "", forModerator: "", anonymous: true });

  const load = async () => setItems((await api.get("/feedback", { params: { meetingId: meeting.id } })).data);
  useEffect(() => { load(); }, [meeting.id]);

  const avg = items.length ? (items.reduce((a, b) => a + Number(b.rating || 0), 0) / items.length).toFixed(1) : null;

  const send = async () => {
    try {
      await api.post("/feedback", { meetingId: meeting.id, ...form });
      setForm({ rating: 8, worked: "", differently: "", forModerator: "", anonymous: true });
      await load();
      toast.success("Pulse sent.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      {avg && (
        <Card className="p-4" data-testid="feedback-average">
          <Kicker>Room average</Kicker>
          <div className="serif text-3xl mt-1">{avg} <span className="text-lg" style={{ color: "var(--ink-mute)" }}>/ 10 · {items.length} responses</span></div>
        </Card>
      )}
      {meeting.status === "Done" && canWrite && (
        <Card data-testid="feedback-form">
          <Kicker>Your pulse</Kicker>
          <div className="mt-4 space-y-4">
            <div>
              <span className="kicker block mb-2">Rating</span>
              <Segmented options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]} value={form.rating} onChange={(v) => setForm({ ...form, rating: Number(v) })} testId="feedback-rating" />
            </div>
            <Area label="What worked" value={form.worked} data-testid="feedback-worked" onChange={(e) => setForm({ ...form, worked: e.target.value })} />
            <Area label="Do differently" value={form.differently} data-testid="feedback-differently" onChange={(e) => setForm({ ...form, differently: e.target.value })} />
            <Field label="One line for the moderator" value={form.forModerator} data-testid="feedback-moderator" onChange={(e) => setForm({ ...form, forModerator: e.target.value })} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="feedback-anon" checked={form.anonymous} onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} />
              Send anonymously
            </label>
            <button className="btn-ink" data-testid="feedback-send" onClick={send}>Send pulse</button>
          </div>
        </Card>
      )}
      {items.length === 0 ? (
        <Empty line="No pulses yet. The room speaks after the close." testId="feedback-empty" />
      ) : (
        items.map((f) => (
          <Card key={f.id} className="p-4" data-testid={`feedback-${f.id}`}>
            <div className="flex justify-between items-baseline gap-3">
              <Chip>{f.memberId ? "Attributed" : "Anonymous"}</Chip>
              <span className="serif text-2xl">{f.rating}</span>
            </div>
            {f.worked && <p className="text-sm mt-2"><span className="kicker">Worked</span><br />{f.worked}</p>}
            {f.differently && <p className="text-sm mt-2"><span className="kicker">Differently</span><br />{f.differently}</p>}
            {f.forModerator && <p className="text-sm mt-2"><span className="kicker">For the moderator</span><br />{f.forModerator}</p>}
          </Card>
        ))
      )}
    </div>
  );
};

/* ------------------------------------------------------------- date poll */
const Poll = ({ meeting, onChange }) => {
  const { me, memberById, canWrite } = useTable();
  const [items, setItems] = useState([]);
  const [date, setDate] = useState("");
  const isAdmin = me.isAdmin && canWrite;

  const load = async () => setItems((await api.get("/poll", { params: { meetingId: meeting.id } })).data);
  useEffect(() => { load(); }, [meeting.id]);

  return (
    <div className="space-y-4">
      {isAdmin && (
        <Card className="p-4">
          <div className="flex gap-2 items-end flex-wrap">
            <div className="flex-1 min-w-[160px]">
              <DateField label="Add a date option" value={date} testId="poll-date" onChange={setDate} />
            </div>
            <button
              className="btn-ink"
              data-testid="poll-add"
              disabled={!date}
              onClick={async () => { await api.post("/poll", { meetingId: meeting.id, dateOption: date }); setDate(""); await load(); }}
            >
              Add
            </button>
          </div>
        </Card>
      )}
      {items.length === 0 ? (
        <Empty line="No dates proposed. The moderator opens the poll." testId="poll-empty" />
      ) : (
        items.map((o) => (
          <Card key={o.id} className="p-4" data-testid={`poll-option-${o.id}`}>
            <div className="flex justify-between gap-3 items-center flex-wrap">
              <div>
                <div className="serif text-xl">{fmtDate(o.dateOption)}</div>
                <div className="flex gap-1.5 mt-2">
                  {(o.voteMemberIds || []).map((id) => {
                    const m = memberById(id);
                    return <Avatar key={id} name={m?.name || "?"} color={m?.avatarColor} size={24} />;
                  })}
                  {(o.voteMemberIds || []).length === 0 && <span className="text-xs" style={{ color: "var(--ink-mute)" }}>No votes yet</span>}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Chip color={o.status === "Finalized" ? "var(--green)" : undefined}>{o.status}</Chip>
                {canWrite && o.status !== "Finalized" && (
                  <button className="btn-quiet" data-testid={`poll-vote-${o.id}`} onClick={async () => { await api.post(`/poll/${o.id}/vote`); await load(); }}>
                    {(o.voteMemberIds || []).includes(me.memberId) ? "Withdraw" : "Vote"}
                  </button>
                )}
                {isAdmin && o.status !== "Finalized" && (
                  <button
                    className="btn-quiet"
                    data-testid={`poll-finalize-${o.id}`}
                    onClick={async () => { await api.post(`/poll/${o.id}/finalize`); await load(); await onChange(); toast.success("Date fixed."); }}
                  >
                    Finalize
                  </button>
                )}
              </div>
            </div>
          </Card>
        ))
      )}
    </div>
  );
};

/* ------------------------------------------------------------- resources tab */
const ChapterResources = ({ meeting }) => {
  const { resources, memberById, yearId, refresh, canWrite } = useTable();
  const items = resources.filter((r) => r.meetingId === meeting.id);
  const [form, setForm] = useState({ title: "", url: "" });

  return (
    <div className="space-y-4">
      {canWrite && (
        <Card className="p-4 space-y-3">
          <Field label="Title" value={form.title} data-testid="chapter-resource-title" onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Field label="URL" value={form.url} data-testid="chapter-resource-url" onChange={(e) => setForm({ ...form, url: e.target.value })} />
          <button
            className="btn-ink"
            data-testid="chapter-resource-save"
            disabled={!form.title || !form.url}
            onClick={async () => {
              await api.post("/resources", { ...form, yearId, meetingId: meeting.id });
              setForm({ title: "", url: "" });
              await refresh();
            }}
          >
            Attach
          </button>
        </Card>
      )}
      {items.length === 0 ? (
        <Empty line="Nothing attached to this chapter." testId="chapter-resources-empty" />
      ) : (
        items.map((r) => (
          <Card key={r.id} className="p-4" data-testid={`chapter-resource-${r.id}`}>
            <div className="serif text-lg">{r.title}</div>
            <a href={r.url} target="_blank" rel="noreferrer" className="text-xs underline" style={{ color: "var(--brass)" }}>{r.url}</a>
            <div className="mt-2"><Chip>{memberById(r.addedById)?.name || "—"}</Chip></div>
          </Card>
        ))
      )}
    </div>
  );
};

/* ------------------------------------------------------------- page */
export default function ChapterDetail() {
  const { id } = useParams();
  const { me, meetings, commitments, activeMembers, memberById, refresh, canWrite, settings } = useTable();
  const [tab, setTab] = useState("The record");
  const [editOpen, setEditOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({});

  const meeting = meetings.find((m) => m.id === id);
  if (!meeting) return <Empty line="No such chapter." />;
  const isAdmin = me.isAdmin && canWrite;
  const emailPaused = !!settings?.email?.paused;

  const toggleAttendance = async (memberId) => {
    if (!isAdmin) return;
    const present = meeting.presentMemberIds || [];
    const next = present.includes(memberId) ? present.filter((x) => x !== memberId) : [...present, memberId];
    await api.put(`/meetings/${meeting.id}`, { presentMemberIds: next });
    await refresh();
  };

  const saveHeader = async () => {
    try {
      await api.put(`/meetings/${meeting.id}`, form);
      setEditOpen(false);
      await refresh();
      toast.success("Chapter updated.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const host = memberById(meeting.deepDiveHostId);
  const scribe = memberById(meeting.scribeId);

  return (
    <div data-testid="chapter-detail">
      <Link to="/chapters" className="kicker no-print">← Chapters</Link>
      <div className="flex gap-5 items-start mt-4 screen-only">
        <span className="roman text-6xl sm:text-7xl">{ROMAN[meeting.num - 1]}</span>
        <div className="min-w-0 flex-1">
          <h1 className="serif text-3xl sm:text-4xl leading-tight">{meeting.title || "Untitled"}</h1>
          <p className="text-sm mt-2" style={{ color: "var(--ink-mute)" }}>
            {fmtDate(meeting.date)}{meeting.time ? ` · ${meeting.time}` : ""}{meeting.venue ? ` · ${meeting.venue}` : ""}
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Chip>{meeting.type}</Chip>
            <Chip color={statusColor(meeting.status)}>{meeting.status}</Chip>
            {meeting.resourceSpeaker && <Chip>Resource · {meeting.resourceSpeaker}</Chip>}
            {host && <Chip>Deep dive · {host.name}</Chip>}
            {scribe && <Chip>Scribe · {scribe.name}</Chip>}
          </div>
        </div>
        {isAdmin && (
          <div className="flex flex-col gap-2 shrink-0 no-print">
            <button className="btn-quiet" data-testid="edit-chapter" onClick={() => { setForm({ ...meeting }); setEditOpen(true); }}>
              Edit
            </button>
            <button
              className="btn-quiet"
              data-testid="print-one-pager"
              onClick={() => window.print()}
            >
              <Printer size={12} /> One-pager
            </button>
            <button
              className="btn-quiet"
              data-testid="send-briefing"
              disabled={sending || emailPaused}
              title={emailPaused ? "Email is paused for the forum" : undefined}
              onClick={async () => {
                setSending(true);
                try {
                  await api.post(`/meetings/${meeting.id}/remind`);
                  toast.success("Briefings are on their way to every member.");
                } catch (e) { toast.error(errMsg(e)); }
                setSending(false);
              }}
            >
              <Send size={12} /> {emailPaused ? "Email paused" : sending ? "Sending" : "Briefing"}
            </button>
          </div>
        )}
      </div>

      <OnePager meeting={meeting} />

      <Card className="mt-6 p-4 screen-only">
        <Kicker>Attendance{isAdmin ? " · tap to mark present" : ""}</Kicker>
        <div className="flex gap-2 mt-3 flex-wrap">
          {activeMembers.map((m) => (
            <button key={m.id} data-testid={`attendance-${m.id}`} onClick={() => toggleAttendance(m.id)} disabled={!isAdmin}>
              <Avatar name={m.name} color={m.avatarColor} size={34} dim={!(meeting.presentMemberIds || []).includes(m.id)} />
            </button>
          ))}
        </div>
      </Card>

      <div className="mt-6 overflow-x-auto no-scrollbar screen-only">
        <div className="flex gap-2 pb-1">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`tab-${t.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setTab(t)}
              className="chip shrink-0"
              style={tab === t ? { background: "var(--ink-panel)", color: "#FFFFFF", borderColor: "var(--ink-panel)" } : {}}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 fade-in screen-only" key={tab}>
        {tab === "The record" && <Highlights meeting={meeting} />}
        {tab === "Ledger" && (
          (() => {
            const items = commitments.filter((c) => c.meetingId === meeting.id);
            return items.length === 0 ? (
              <Empty line="Nothing here yet. The close writes this page." testId="chapter-ledger-empty" />
            ) : (
              <div className="space-y-3">
                {items.map((c) => (
                  <CommitmentRow key={c.id} c={c} refresh={refresh} canEdit={canWrite && (me.isAdmin || c.memberId === me.memberId)} />
                ))}
              </div>
            );
          })()
        )}
        {tab === "Date poll" && <Poll meeting={meeting} onChange={refresh} />}
      </div>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit chapter" testId="chapter-edit-modal">
        <Field label="Title" value={form.title || ""} data-testid="chapter-title" onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <DateField label="Date" value={form.date || ""} testId="chapter-date" onChange={(v) => setForm({ ...form, date: v })} />
        <Field label="Time" value={form.time || ""} onChange={(e) => setForm({ ...form, time: e.target.value })} />
        <Field label="Venue" value={form.venue || ""} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
        <Field label="Resource speaker" value={form.resourceSpeaker || ""} onChange={(e) => setForm({ ...form, resourceSpeaker: e.target.value })} />
        <Pick label="Type" options={MEETING_TYPES} value={form.type || "Regular"} onChange={(e) => setForm({ ...form, type: e.target.value })} />
        <Pick label="Status" options={["Planned", "Scheduled", "Done"]} value={form.status || "Planned"} data-testid="chapter-status" onChange={(e) => setForm({ ...form, status: e.target.value })} />
        <Pick
          label="Deep-dive host"
          options={[{ value: "", label: "—" }, ...activeMembers.map((m) => ({ value: m.id, label: m.name }))]}
          value={form.deepDiveHostId || ""}
          onChange={(e) => setForm({ ...form, deepDiveHostId: e.target.value })}
        />
        <Pick
          label="Scribe"
          options={[{ value: "", label: "—" }, ...activeMembers.map((m) => ({ value: m.id, label: m.name }))]}
          value={form.scribeId || ""}
          onChange={(e) => setForm({ ...form, scribeId: e.target.value })}
        />
        <Area label="Notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <button className="btn-ink w-full" data-testid="chapter-save" onClick={saveHeader}>Save</button>
      </Modal>
    </div>
  );
}
